import { useOptionalContainer, useOptionalPlayer } from "../../player/context.js";
import { useOptionalPopupGroup } from "../../player/popup-group-context.js";
import { useLatestRef } from "../../utils/use-latest-ref.js";
import { useDestroy } from "../../utils/use-destroy.js";
import { MenuContextProvider, useOptionalMenuContext } from "./context.js";
import { useSafeId } from "../../utils/use-safe-id.js";
import { useOptionalControlsContext } from "../controls/context.js";
import { usePositionedState } from "../hooks/use-positioned-state.js";
import { MenuCore, resolveMenuOptionState } from "@videojs/core";
import { createMenu, createTransition, selectControls } from "@videojs/core/dom";
import { useSnapshot } from "@videojs/store/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/ui/menu/root.tsx
function MenuRoot({ open: controlledOpen, defaultOpen = MenuCore.defaultProps.defaultOpen, onOpenChange: onOpenChangeProp, onOpenChangeComplete: onOpenChangeCompleteProp, boundary = "container", children, ...coreProps }) {
	const parentMenu = useOptionalMenuContext();
	const controls = useOptionalControlsContext();
	const controlsState = useOptionalPlayer(selectControls);
	const container = useOptionalContainer();
	const popupGroup = useOptionalPopupGroup();
	const isSubmenu = parentMenu !== null;
	const { side, align, closeOnEscape, closeOnOutsideClick } = coreProps;
	const [core] = useState(() => new MenuCore(coreProps));
	const isControlled = controlledOpen !== void 0;
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const resolvedOpen = controlledOpen ?? uncontrolledOpen;
	const onOpenChangeRef = useLatestRef(onOpenChangeProp);
	const onOpenChangeCompleteRef = useLatestRef(onOpenChangeCompleteProp);
	const closeOnEscapeRef = useLatestRef(closeOnEscape);
	const closeOnOutsideClickRef = useLatestRef(closeOnOutsideClick);
	const popupGroupRef = useLatestRef(popupGroup);
	const isSubmenuRef = useLatestRef(isSubmenu);
	const [menu] = useState(() => {
		return createMenu({
			transition: createTransition(),
			onOpenChange(nextOpen, details) {
				if (!isControlled) setUncontrolledOpen(nextOpen);
				onOpenChangeRef.current?.(nextOpen, details);
			},
			onOpenChangeComplete(nextOpen) {
				onOpenChangeCompleteRef.current?.(nextOpen);
			},
			closeOnEscape: () => closeOnEscapeRef.current ?? MenuCore.defaultProps.closeOnEscape,
			closeOnOutsideClick: () => closeOnOutsideClickRef.current ?? MenuCore.defaultProps.closeOnOutsideClick,
			group: () => isSubmenuRef.current ? void 0 : popupGroupRef.current
		});
	});
	const anchorName = useSafeId();
	const contentId = useSafeId("menu");
	useLayoutEffect(() => parentMenu?.menu.registerSubmenu(menu), [menu, parentMenu?.menu]);
	useLayoutEffect(() => {
		menu.syncOpen(resolvedOpen);
	}, [menu, resolvedOpen]);
	useEffect(() => {
		if (isSubmenu || controls?.state.visible !== false) return;
		menu.close("imperative-action");
	}, [
		controls?.state.visible,
		isSubmenu,
		menu
	]);
	useDestroy(menu);
	const input = useSnapshot(menu.input);
	useEffect(() => {
		if (!input.active || isSubmenu) return;
		return controlsState?.requestControlsLock();
	}, [
		controlsState?.requestControlsLock,
		input.active,
		isSubmenu
	]);
	const preferredState = useMemo(() => {
		core.setProps({
			side,
			align,
			closeOnEscape,
			closeOnOutsideClick
		});
		core.setInput({
			...input,
			isSubmenu
		});
		return core.getState();
	}, [
		core,
		input,
		side,
		align,
		closeOnEscape,
		closeOnOutsideClick,
		isSubmenu
	]);
	const { state, preferredSide, setPositionedSide } = usePositionedState(preferredState);
	const [optionStates, setOptionStates] = useState(() => /* @__PURE__ */ new Map());
	const [optionSource] = useState(() => Symbol("menu"));
	const setOptionState = useCallback((source, optionState) => {
		setOptionStates((current) => {
			const previous = current.get(source);
			if (optionState && isSameOptionState(previous, optionState)) return current;
			if (!optionState && !previous) return current;
			const next = new Map(current);
			if (optionState) next.set(source, optionState);
			else next.delete(source);
			return next;
		});
	}, []);
	const optionState = useMemo(() => resolveMenuOptionState(optionStates.values()), [optionStates]);
	const setParentOptionState = parentMenu?.setOptionState;
	useEffect(() => {
		if (!optionState?.disabled && !optionState?.hidden) return;
		menu.close("imperative-action");
	}, [
		menu,
		optionState?.disabled,
		optionState?.hidden
	]);
	useLayoutEffect(() => {
		if (!setParentOptionState) return void 0;
		setParentOptionState(optionSource, optionState);
		return () => setParentOptionState(optionSource, null);
	}, [
		optionSource,
		optionState,
		setParentOptionState
	]);
	const contextValue = useMemo(() => ({
		core,
		menu,
		parent: parentMenu,
		state,
		preferredSide,
		setPositionedSide,
		contentId,
		anchorName,
		boundary,
		container,
		optionState,
		setOptionState
	}), [
		core,
		menu,
		parentMenu,
		state,
		preferredSide,
		setPositionedSide,
		contentId,
		anchorName,
		boundary,
		container,
		optionState,
		setOptionState
	]);
	return /* @__PURE__ */ jsx(MenuContextProvider, {
		value: contextValue,
		children
	});
}
function isSameOptionState(a, b) {
	return a?.value === b.value && a.disabled === b.disabled && a.hidden === b.hidden && a.availability === b.availability;
}
//#endregion
export { MenuRoot };

//# sourceMappingURL=root.js.map